import React, { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import { Typography, Card } from "antd";
import moment from 'moment';
import AnyaVisualizationLine from './AnyaVizualizationComponents/AnyaVisualizationLine';
import AnyaVisualizationPie from './AnyaVizualizationComponents/AnyaVisualizationPie';
import AnyaVisualizationTable from './AnyaVizualizationComponents/AnyaVisualizationTable';
import AnyaVisualizationBar from './AnyaVizualizationComponents/AnyaVisualizationBar';
import AnyaVisualizationCodeBlock from './AnyaVizualizationComponents/AnyaVisualizationCodeBlock';
import AnyaWidgetRenderer from './AnyaVizualizationComponents/AnyaWidgetRenderer';
import StreamingText from './StreamingText';
import AnyaAPI from 'restAPI/AnyaAPI';
import AnyaChatLoadingIcon from 'ac-components/ac-icons/AnyaChatLoadingIcon';
import { useFeatureFlag } from 'contexts/FeatureFlagContext';
import { WidgetApiInterceptorProvider } from 'contexts/WidgetApiInterceptorContext';
import {
    isStatusInProgress,
    isResponseCompleted,
    isStatusAwaitingConfirmation,
    shouldShowDetails,
    shouldShowReasoningSummary,
    hasPollingTimedOut
} from './anyaUtils';
import { ANYA_POLLING_CONFIG } from './anyaConstants';

/**
 * Check if visualization is a widget-based visualization (has widgetId)
 */
const isWidgetVisualization = (v) => v && v.widgetId && v.widgetType;
const userLocale = navigator.language || "en-US";
const userZoneId = moment.tz.guess();
const AnyaMessage = ({ setIsGeneratingResponse, fetchChatHistory, isNewChat: _isNewChat, message, index, COLORS, setSuggestedQuestions, chatId, setChatId, title, widgetType, dashboardId, setShowThumbsDownReasonTextArea, setMessageId, onResponseDetails, setShowTicketConfirmation, setConfirmationData, setConfirmationRequestId, showTicketConfirmation, onRequestStart, onRequestComplete, onRetryComplete }) => {

    const featureFlags = useFeatureFlag();
    const [response, setResponse] = useState(message.response);
    const [showVisualization, setShowVisualization] = useState(false);
    const hasResponse = useMemo(() => message.response !== null,[message]);

    const getDisplayStatusText = useCallback(() => {
        // Use message displayStatus for conversation history
        if (message?.displayStatus && message.displayStatus.toLowerCase() !== 'completed') {
            return message.displayStatus;
        }
        // Use response displayStatus for new Assistant API calls
        else if (response?.displayStatus && response.displayStatus.toLowerCase() !== 'completed') {
            return response.displayStatus;
        }

        return null;
    }, [response, message]);
    const [query, setQuery] = useState("");
    const [requestId, setRequestId] = useState(null);
    const [currentConversationId,setCurrentConversationId] = useState(chatId);
    const [isPolling, setIsPolling] = useState(false);
    const normalizedTitle = title || "";
    const initialCallMade = useRef(false);
    const pollingStartTimeRef = useRef(null);
    const pollTimerRef = useRef(null);

    useEffect(() => {
            if (!showTicketConfirmation && requestId && response && !isResponseCompleted(response)) {
                setIsPolling(true);
            }
        }, [showTicketConfirmation, requestId, response]);

    useEffect(() => {
        const checkStatus = async (requestId) => {
            // Stop polling if message is cancelled
            if (message.isCancelled) {
                setIsPolling(false);
                return;
            }
            // Check timeout - elapsed time since polling started
            if (hasPollingTimedOut(pollingStartTimeRef.current, ANYA_POLLING_CONFIG.MAX_POLL_TIME)) {
                console.warn("Max poll time reached. Stopping polling.");
                if (pollTimerRef.current) {
                    clearTimeout(pollTimerRef.current);
                }
                setIsPolling(false);
                setResponse({reasoningSummary:"Unable to generate response, please try again."});
                setIsGeneratingResponse(false);
                // Notify parent that request is complete (timed out)
                if (onRequestComplete) {
                    onRequestComplete();
                }
                return;
            }
            try {
                    const { promise } = AnyaAPI.getAnyaMessage({
                        conversationId: currentConversationId,
                        messageId: requestId
                    });
                    const { data } = await promise;

                    // Always update response to capture displayStatus during polling
                    setResponse({ ...data, requestId });

                    // Set messageId as soon as we have a requestId, regardless of status
                    if (!message.hasOwnProperty("messageId")) {
                        message.messageId = requestId;
                    }
                    setMessageId(requestId);

                    if (isStatusAwaitingConfirmation(data)) {
                        setConfirmationData(data.eventData);
                        setShowTicketConfirmation(true);
                        setIsPolling(false);  
                        setConfirmationRequestId(requestId);
                        if (pollTimerRef.current) {
                            clearTimeout(pollTimerRef.current);
                        }
                    }
                    else if (isResponseCompleted(data)) {
                        if (pollTimerRef.current) {
                            clearTimeout(pollTimerRef.current);
                        }
                        setIsPolling(false);
                        setIsGeneratingResponse(false);
                        onResponseDetails(requestId, data.details);
                                             
                        if (message.isRetrying && onRetryComplete) {
                            onRetryComplete(requestId);
                        }

                        // Notify parent that request is complete
                        if (onRequestComplete) {
                            onRequestComplete();
                        }
                    }
                    else {
                        // Continue polling - schedule next poll with 1s interval
                        pollTimerRef.current = setTimeout(() => {
                            checkStatus(requestId);
                        }, ANYA_POLLING_CONFIG.SUBSEQUENT_POLL_INTERVAL);
                    }

            } catch (error) {
                console.error("Error fetching status:", error);
                setIsGeneratingResponse(false);
                setResponse({reasoningSummary:"Something went wrong, please try again."});
                if (pollTimerRef.current) {
                    clearTimeout(pollTimerRef.current);
                }
                setIsPolling(false);
                // Notify parent that request is complete (error)
                if (onRequestComplete) {
                    onRequestComplete();
                }
            }
        };


        if (requestId && isPolling && !message.isCancelled) {
            if (!pollingStartTimeRef.current) {
                pollingStartTimeRef.current = Date.now();
            }
            // Schedule first poll after 4 seconds
            pollTimerRef.current = setTimeout(() => {
                checkStatus(requestId);
            }, ANYA_POLLING_CONFIG.INITIAL_POLL_DELAY);
        }

        return () => {
            if (pollTimerRef.current) {
                clearTimeout(pollTimerRef.current);
            }
        };
    }, [currentConversationId, dashboardId, isPolling, message, message.isCancelled, message.isRetrying, normalizedTitle, onRequestComplete, onResponseDetails, onRetryComplete, query, requestId, setConfirmationData, setConfirmationRequestId, setIsGeneratingResponse, setMessageId, setShowTicketConfirmation, title, widgetType]);
    const getAssistant = useCallback((userQuery) => {
        if (!requestId) {
            const { cancel, promise } = AnyaAPI.getAnyaAssistant({ query: userQuery, conversation_history: [], widgetTitle:normalizedTitle, widgetType, dashboardId, conversationId: currentConversationId, locale:userLocale, zoneId: userZoneId});
            setIsGeneratingResponse(true);
            
            // Store cancel function for potential cancellation
            if (onRequestStart) {
                onRequestStart(cancel);
            }
            
            promise.then((res) => {
                const response = res.data;

                // Always update response to capture displayStatus from the very first call
                setResponse(response);

                // Set messageId as soon as we have a requestId, regardless of status
                if (response?.requestId) {
                    if (!message.hasOwnProperty("messageId")) {
                        message.messageId = response.requestId;
                    }
                    setMessageId(response.requestId);
                }

                if (response?.status && !isStatusInProgress(response)) {
                    setIsGeneratingResponse(false);
                    if (onRequestComplete) {
                        onRequestComplete();
                    }
                }
                else {
                    setQuery(userQuery)
                    pollingStartTimeRef.current = null;
                    setIsPolling(true);
                }
                setRequestId(response.requestId);
                setCurrentConversationId(response.conversationId);
                
                // Notify parent about request details for cancellation
                if (onRequestStart) {
                    onRequestStart(cancel, response.requestId, response.conversationId);
                }
                
                // Always update chatId to the new conversation (whether new or continuing)
                setChatId(response.conversationId);
                // Always fetch chat history when a new conversation is created
                // This ensures the history sidebar shows the latest conversations
                fetchChatHistory(false)
                setShowThumbsDownReasonTextArea((prev) => ({ ...prev, [response.requestId]: false }))
            })
                .catch((error) => {
                    setResponse({reasoningSummary:"Something went wrong, please try again."});
                    setIsGeneratingResponse(false);
                    if (onRequestComplete) {
                        onRequestComplete();
                    }
                })
            return cancel;
        }
    }, [requestId, widgetType, dashboardId, currentConversationId, setIsGeneratingResponse, setShowThumbsDownReasonTextArea, setChatId, fetchChatHistory, onRequestStart, onRequestComplete, message, normalizedTitle, setMessageId])

    const handleCloseCodeBlock = useCallback((indexToClose) => {
        setResponse(prevResponse => {
            if (!prevResponse) return null;
    
            // Create a new array of visualizations, excluding the one at the specified index
            const updatedVisualisations = prevResponse.visualisation.filter((_, index) => index !== indexToClose);
    
            // Return a new response object with the updated visualizations
            return {
                ...prevResponse,
                visualisation: updatedVisualisations,
            };
        });
    }, []);

    const displayVisualizations = useCallback(() => {
        setShowVisualization(true);
        setIsGeneratingResponse(false)
        setSuggestedQuestions(response?.nextQuestions??[])
    }, [setIsGeneratingResponse,response,setSuggestedQuestions]);
    
    useEffect(() => {
        if (!hasResponse && !message.isRetrying && !message.isCancelled && !initialCallMade.current) {
            initialCallMade.current = true;
            getAssistant(message.userMessage?.text);
        }
        else if (hasResponse && !isStatusInProgress(message)) {
            setResponse(message.response);
        }
        else if (message.isRetrying) {
            setIsGeneratingResponse(true);
            setResponse(null);
            setRequestId(message.messageId);
            // Reset polling start time so the 150s timeout restarts for the retry
            pollingStartTimeRef.current = null;
            setIsPolling(true);

            if (onRequestStart) {
                onRequestStart(null, message.messageId, currentConversationId);
            }
        }
        else if (message.isCancelled) {
            // Handle cancelled message - STOP all polling immediately
            setResponse({
                ...message.response,
                reasoningSummary: "Request Cancelled",
                status: "CANCELLED"
            });
            setIsPolling(false);
            setIsGeneratingResponse(false);
            if (onRequestComplete) {
                onRequestComplete();
            }
        }
        else if (isStatusInProgress(message)) {
            setIsGeneratingResponse(true);
            setResponse(null);
        }
    }, [getAssistant, message, hasResponse, setIsGeneratingResponse, onRequestComplete, currentConversationId, onRequestStart])
    return <div key={index} className="message-container">
        <Card className="user-message" style={{ backgroundColor: '#f0f0f0', textAlign: 'right', display: 'inline-block', width: 'auto', marginRight:'15px' }}>
            <Typography.Text>{message.userMessage?.text}</Typography.Text>
        </Card>
        <Card className="system-message" >
            {!featureFlags.AI_ANYA_REMEDIATION_ACTIONS_DISABLED && featureFlags.AI_ANYA_REMEDIATION_ACTIONS && response?.visualisation?.map((v, index) => {
                if (v.type === 'code') {
                    return <AnyaVisualizationCodeBlock showVisualization={hasResponse || showVisualization} key={index} visualisation={v} onClose={() => handleCloseCodeBlock(index)}  />
                }
            })
        }
            <div style={{ display: 'flex', alignItems: 'center' }}>
                {!showTicketConfirmation && (!response || isStatusInProgress(response)) && (
                    <Typography.Text
                        className='system-process'
                    >
                        {getDisplayStatusText()} <AnyaChatLoadingIcon/>
                    </Typography.Text>
                )}
                {!showTicketConfirmation && shouldShowReasoningSummary(response, message.isCancelled) && (
                    <Typography.Text className="static-message">{response?.reasoningSummary}</Typography.Text>
                )}
            </div>
            {shouldShowDetails(response) && <StreamingText
                text={response?.details}
                stream={!hasResponse}
                onComplete={displayVisualizations}
            />}
            {response?.visualisation?.map((v, index) => {
                // Handle widget-based visualization (when widgetId is present)
                if (isWidgetVisualization(v)) {
                    const messageId = requestId || message.messageId;

                    return (
                        <WidgetApiInterceptorProvider
                            key={v.widgetId}
                            preFetchedData={v.data}
                            conversationId={currentConversationId}
                            messageId={messageId}
                            widgetId={v.widgetId}
                        >
                            <AnyaWidgetRenderer
                                widgetId={v.widgetId}
                                widgetType={v.widgetType}
                                displayType={v.displayType}
                                config={{...v.config, defaultSortInfo: v.sortInfo}}
                                filters={v.filters}
                                title={v.title}
                                description={v.description}
                                showVisualization={hasResponse || showVisualization}
                            />
                        </WidgetApiInterceptorProvider>
                    );
                }
                // Existing visualization types
                if (v.type === 'chart') {
                    return <AnyaVisualizationPie showVisualization={hasResponse || showVisualization} key={index} visualisation={v} COLORS={COLORS} />
                } else if (v.type === 'trend') {
                    return <AnyaVisualizationLine showVisualization={hasResponse || showVisualization} key={index} visualisation={v} COLORS={COLORS} />
                } else if (v.type === 'table') {
                    return <AnyaVisualizationTable showVisualization={hasResponse || showVisualization} key={index} visualisation={v} COLORS={COLORS} />
                } else if (v.type === 'bar') {
                    return <AnyaVisualizationBar showVisualization={hasResponse || showVisualization} key={index} visualisation={v} COLORS={COLORS} />
                }
                else {
                    return '';
                }
            })

            }

        </Card>
    </div>
}
// Memoize AnyaMessage to prevent re-renders when parent state changes
// (e.g., typing in AnyaConversation input, toggling history sidebar)
export default memo(AnyaMessage);